import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import experienceData from '../data/experience.json';

type Props = {};

const OuterLink = (): React.JSX.Element => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="#a1ceef"
			className="min-w-[16px] min-h-[16px] w-4 h-4">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
			/>
		</svg>
	);
};

export default function ExperienceSection({}: Props) {
	function formatDateDifference(startDate: Dayjs, endDate: Dayjs): string {
		const years: number = endDate.diff(startDate, 'year');
		const months: number =
			endDate.diff(startDate.add(years, 'year'), 'month') + 1;
		const days: number = endDate.diff(
			startDate.add(years, 'year').add(months, 'month'),
			'day'
		);

		const pluralize = (value: number, unit: string): string =>
			value === 1 ? `${value} ${unit}` : `${value} ${unit}s`;

		const parts: string[] = [];
		if (years > 0) parts.push(pluralize(years, 'year'));
		if (months > 0) parts.push(pluralize(months, 'month'));
		if (days > 0) parts.push(pluralize(days, 'day'));

		return parts.join(' ');
	}

	return (
		<section id="experience">
			<div className="experience--section">
				<h2 className="section--title">Experience</h2>
				<div className="section--content flex flex-col gap-24 experience--content">
					<div
						className="category--no--links category"
						data-aos="fade-down"
						data-aos-duration="1000"
						data-aos-once="true">
						<h2>Employment History</h2>
						<ul className="experience--list">
							{experienceData.employment.map((job) => (
								<li key={job.name}>
									{job.url ? (
										<a
											href={job.url}
											className="anim--link"
											target="_blank"
											rel="noreferrer">
											<h3>
												{job.name}
												<OuterLink />
											</h3>
										</a>
									) : (
										<h3>{job.name}</h3>
									)}
									<div className="flex flex-col gap-1">
										<p>
											<strong>Time span:</strong>{' '}
											<i>
												{dayjs(job.startDate).format('DD.MM.YYYY')} -{' '}
												{job.endDate
													? dayjs(job.endDate).format('DD.MM.YYYY')
													: 'now'}{' '}
												<small>
													{' '}
													(
													{formatDateDifference(
														dayjs(job.startDate),
														job.endDate ? dayjs(job.endDate) : dayjs()
													)}
													)
												</small>
											</i>
										</p>
										<p>
											<strong>Role:</strong> <i>{job.role}</i>
										</p>
										<p>
											<strong>Employment type:</strong>{' '}
											<i>{job.employmentType}</i>
										</p>
									</div>
								</li>
							))}
						</ul>
					</div>
					<div
						className="category--no--links category"
						data-aos="fade-down"
						data-aos-duration="1000"
						data-aos-once="true">
						<h2>Internships</h2>
						<ul className="experience--list">
							{experienceData.internships.map((internship) => (
								<li key={internship.name}>
									{internship.url ? (
										<a
											href={internship.url}
											className="anim--link"
											target="_blank"
											rel="noreferrer">
											<h3>
												{internship.name}
												<OuterLink />
											</h3>
										</a>
									) : (
										<h3>{internship.name}</h3>
									)}
									<div className="flex flex-col gap-1">
										<p>
											<strong>Time span:</strong>{' '}
											<i>
												{dayjs(internship.startDate).format('DD.MM.YYYY')} -{' '}
												{internship.endDate
													? dayjs(internship.endDate).format('DD.MM.YYYY')
													: 'now'}{' '}
												<small>
													{' '}
													(
													{formatDateDifference(
														dayjs(internship.startDate),
														internship.endDate
															? dayjs(internship.endDate)
															: dayjs()
													)}
													)
												</small>
											</i>
										</p>
										{internship.description && (
											<p
												dangerouslySetInnerHTML={{
													__html: internship.description,
												}}
											/>
										)}
									</div>
								</li>
							))}
						</ul>
					</div>
					<div
						className="category"
						data-aos="fade-down"
						data-aos-duration="1000"
						data-aos-once="true">
						<h2>Courses</h2>
						<div className="category--links courses--list">
							<a
								href="https://indekswkieszeni.pl/olimpiada-informatyczna/"
								className="anim--link"
								target="_blank"
								rel="noreferrer">
								Algorithmics, Indeks w Kieszeni
								<OuterLink />
							</a>
							<a
								href="https://scrimba.com/learn/learnreact"
								className="anim--link"
								target="_blank"
								rel="noreferrer">
								Learn React, Scrimba
								<OuterLink />
							</a>
							<a
								href="https://www.udemy.com/course/unrealcourse/"
								className="anim--link"
								target="_blank"
								rel="noreferrer">
								Unreal Engine 5, Udemy
								<OuterLink />
							</a>
						</div>
					</div>
					<div
						className="category--no--links category"
						data-aos="fade-down"
						data-aos-duration="1000"
						data-aos-once="true">
						<h2>What I've done/used</h2>
						<ul>
							<li>Small games in Unreal Engine C++</li>
							<li>Desktop apps and visualizations in Qt C++</li>
							<li>Tailwind CSS usage throughout my web projects</li>
							<li>Firebase, Firestore and Firebase OAuth</li>
							<li>AWS, Google Analytics and Cloudflare</li>
							<li>Scripts and applications with API</li>
							<li>
								Lots of algorithmic type problems solved (Binary Search, DFS,
								BFS, Dijkstra, Interval tree's algorithms)
							</li>
							<li>
								Advanced knowledge of the C++ STL library and sorting algorithms
							</li>
							<li>Custom API's (Express.js)</li>
							<li>Various webscrapers (selenium.js, cypress.js)</li>
							<li>Lektor CMS</li>
							<li>Software testing</li>
						</ul>
					</div>
				</div>
			</div>
		</section>
	);
}
